import { isNumber, memoizeFunction } from '@agilepoint/utils-common';

export interface INumericUtilityProps extends INumberSeparator {
    blockDecimal?: boolean;
    maxDecimalPlaces?: number;
    decimalPlaces?: number;
    max?: number;
    min?: number;
}

interface INumberSeparator {
    separator: string;
    decimalCharacter: string;
}

interface INumberFormatOptions {
    withSeparator: boolean;
    blockDecimal?: boolean;
    culture?: string;
    decimalCharacter: string;
    decimalPlaces?: number;
    maxDecimalPlaces?: number;
}

export const SERVER_CULTURE = 'en-US';
const AUTO_SELECT_CULTURE = 'auto';

function splitNumber(value: string, decimalCharacter = '.'): string[] {
    return value.split(decimalCharacter);
}

const getCulture = (culture?: string) => (culture !== AUTO_SELECT_CULTURE ? culture : undefined);

const getSeparatorAndDecimalChar = memoizeFunction((culture?: string): string[] => {
    try {
        const sampleValue = 123123.123.toLocaleString(getCulture(culture));
        const matchedCharacters = sampleValue.match(/[^\d-]+/g);
        if (matchedCharacters && matchedCharacters.length === 2) {
            return matchedCharacters;
        }
        return [',', '.'];
    } catch (e) {
        return [',', '.'];
    }
});

function addSeparator(value: string, culture?: string): string {
    try {
        value = numericUtility.convertToCulture(value, culture, SERVER_CULTURE);
        return Number(value).toLocaleString(getCulture(culture));
    } catch (e) {
        return Number(value).toLocaleString(SERVER_CULTURE);
    }
}

function removeSeparator(value: string, separator: string): string {
    value = value.trim();
    if (value.includes(separator)) {
        return value.split(separator).join('');
    }
    return value;
}

function convertToArabicNumber(wholeNumber: string, fraction: string): string {
    return `${wholeNumber}${fraction ? `.${fraction}` : ''}`;
}

function hasPositiveSign(value: string): boolean {
    return value.indexOf('+') > -1;
}

function trimFractionWithMaxDecimalPlaces(fraction: string, maxDecimalPlaces?: number) {
    if (Number(maxDecimalPlaces) < 1 || fraction.length === 0) {
        return '';
    }
    if (!isNumber(maxDecimalPlaces) || fraction.length <= maxDecimalPlaces) {
        return fraction;
    }
    return fraction.substring(0, maxDecimalPlaces);
}

function trimFractionWithDecimalPlaces(fraction: string, decimalPlaces?: number) {
    if (!isNumber(decimalPlaces) || fraction.length === decimalPlaces) {
        return fraction;
    }

    if (decimalPlaces < 1) {
        return '';
    }

    fraction = fraction.substring(0, decimalPlaces);
    while (fraction.length < decimalPlaces) {
        fraction += '0';
    }

    return fraction;
}

function getNumberAsString(value: string, formatOptions: INumberFormatOptions): string {
    if (value.length === 0) {
        return value;
    }
    const {
        blockDecimal,
        decimalCharacter,
        culture,
        maxDecimalPlaces,
        decimalPlaces,
        withSeparator
    } = formatOptions;

    let [wholeNumber, fraction = ''] = splitNumber(value, decimalCharacter);

    if (withSeparator) {
        wholeNumber = addSeparator(wholeNumber, culture);
    }
    if (blockDecimal || maxDecimalPlaces === 0 || decimalPlaces === 0) {
        return wholeNumber;
    }
    fraction = trimFractionWithDecimalPlaces(fraction, decimalPlaces);
    fraction = trimFractionWithMaxDecimalPlaces(fraction, maxDecimalPlaces);
    return `${wholeNumber}${fraction.length > 0 ? decimalCharacter : ''}${fraction}`;
}

const numericUtility = {
    isValid(value: string, props: INumericUtilityProps) {
        const { max, min, separator, maxDecimalPlaces = Infinity, decimalPlaces = Infinity, decimalCharacter } = props;
        const arabicNumSysDecimalChar = '.';
        if (value === decimalCharacter || value === '-' || value === '') {
            return true;
        }
        if (decimalCharacter !== arabicNumSysDecimalChar && value.indexOf(arabicNumSysDecimalChar) >= 0) {
            return false;
        }
        const [wholeNumber, fraction, error] = splitNumber(value, decimalCharacter);
        if (error !== undefined) {
            return false;
        }
        if (fraction) {
            const isFractionInRange = this.isFractionInRange(fraction.substring(0, Math.min(decimalPlaces, maxDecimalPlaces)), maxDecimalPlaces, decimalPlaces);
            if (!isFractionInRange) {
                return false;
            }
        }
        const newValue = convertToArabicNumber(removeSeparator(wholeNumber, separator), fraction);
        const isValidNumber = this.isValidNumber(newValue, max, min);
        if (!isValidNumber) {
            return false;
        }
        return true;
    },
    isValidNumber(value: string, max?: number, min?: number) {
        const isNan = isNaN(Number(value));
        if (value === '' || isNan) {
            return false;
        }
        const isInRange = this.isNumberInRange(value, max, min);
        const isPositive = hasPositiveSign(value);
        if (isPositive || !isInRange) {
            return false;
        }
        return true;
    },

    isFractionInRange(fraction: string, maxDecimalPlaces?: number, decimalPlaces?: number): boolean {
        if (isNumber(maxDecimalPlaces)) {
            return fraction.length <= maxDecimalPlaces;
        }
        return isNumber(decimalPlaces) ? fraction.length === decimalPlaces : true;
    },

    isNumberInRange(value: string, max: number = Number.MAX_VALUE, min: number = -Number.MAX_VALUE): boolean {
        const numericValue = Number(value);
        return numericValue <= max && numericValue >= min;
    },

    getFallBackValue(value: string, props: INumericUtilityProps): string {

        const { max, min, decimalCharacter = '.' } = props;

        if (decimalCharacter !== '.' && value.includes(decimalCharacter)) {
            return value.replace('.', '');
        }

        const numericValue = Number(value);

        if (isNaN(numericValue) || hasPositiveSign(value)) {
            return '';
        }
        if (numericValue > max!) {
            return max!.toString();
        }
        if (numericValue < min!) {
            return min!.toString();
        }
        return '';
    },

    convertToCulture(value: string, fromCulture?: string, toCulture?: string) {
        if (fromCulture === toCulture) {
            return value;
        }
        const [separator, decimalCharacter] = getSeparatorAndDecimalChar(fromCulture);
        const [, serverDecimalCharacter] = getSeparatorAndDecimalChar(toCulture);
        return value.replaceAll(separator, '').replace(decimalCharacter, serverDecimalCharacter);
    }
};

function getFallbackMinValue(min?: number, max?: number) {
    if (!(min === undefined || min === -Number.MAX_VALUE)) {
        return min;
    }
    if (!(max === undefined || max === Number.MAX_VALUE)) {
        return Math.min(0, max);
    }
    return 0;
}

export {
    getCulture,
    splitNumber,
    getSeparatorAndDecimalChar,
    addSeparator,
    removeSeparator,
    getNumberAsString,
    getFallbackMinValue,
    numericUtility,
    trimFractionWithMaxDecimalPlaces,
    trimFractionWithDecimalPlaces
};
